import {json, type LoaderFunction} from '@remix-run/node'
import {Link, useLoaderData} from '@remix-run/react'
import {textToSlug} from '@roundforest/text-transforms-commons'
import {fetchL1Categories} from '~/api/home-page-fetcher.server'
import {IconRightArrowLightWeight} from '~/icons/home-page/brg-icon-app-logo-thick'
import HomeCategoriesHeader from '~/components/home-categories/home-categories-header'
import type {L1CategoryProps} from '~/types'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'

import {fakeL1Categories} from '~/fixtures/home-page-popular-categories-l1'
import {setEnvironmentWith} from '~/utils/async-storage.server'

export {meta} from '../_index/route'

export const handle = {
  breadcrumb: 'topCategory',
}

export const loader: LoaderFunction = async ({params, context}) => {
  setEnvironmentWith(context)

  const topCategory = textToSlug(params?.topCategory || '')

  if (process.env.NODE_ENV === 'development') {
    return json(fakeL1Categories)
  }

  const response = await fetchL1Categories({slug: topCategory})

  if (response.type === 'success' && response?.value) {
    return json<L1CategoryProps>(response?.value)
  } else {
    throw new Response('Not Found', {status: 404})
  }
}

const Categories = () => {
  const categories = useLoaderData<L1CategoryProps>()
  const sendWidgetClickEvent = useReportWidgetClick()
  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'home-page-grid-level-2',
    widget_place: 'main',
  })

  return (
    <>
      <HomeCategoriesHeader />
      <main className="flex w-full justify-center bg-white" ref={widgetRef}>
        <div className="content-container pt-14 text-2xl font-bold text-[#494747]">
          <div className="mb-14 grid grid-cols-1 gap-10 desktop:grid-cols-3 tablet:grid-cols-2 tablet:gap-10 mobile:grid-cols-1">
            {categories?.l2Categories?.map(({slug, image, name, l3Categories}, index) => (
              <div
                className="mb-4 flex flex-col"
                key={`${name}-${index}`}
                data-testid={`l1-categories-list-item-${index + 1}`}
              >
                <Link
                  reloadDocument
                  to={`/categories/${categories.slug}/${slug}`}
                  className="flex min-h-80 w-full flex-col items-start justify-between rounded-lg bg-[#F5F5F5] px-5 py-2"
                  aria-label={`go to ${name} category`}
                  onClick={() => {
                    sendWidgetClickEvent({
                      widget_name: `framed-image-category-${name}`,
                      widget_place: 'grid-item',
                      trigger_type: `framed-image`,
                    })
                  }}
                >
                  <img
                    src={image}
                    alt={name}
                    className="mb-10 flex h-44 w-full shrink-0 grow-0 object-contain p-5 mix-blend-darken"
                  />
                  <p className="mb-2 mt-auto line-clamp-1 overflow-hidden pt-4 text-base font-bold leading-7 text-black">
                    {name}
                  </p>
                </Link>
                <div className="flex flex-col [&>*]:border-b [&>*]:border-b-neutral-200">
                  {l3Categories.slice(0, 3).map(({name, slug}, index) => {
                    return (
                      <Link
                        reloadDocument
                        key={`${slug}-${index}`}
                        className="flex flex-row items-center justify-between p-4 text-sm font-normal text-black hover:text-neutral-500"
                        to={`/${slug}`}
                        aria-label={`go to ${name}`}
                        onClick={() => {
                          sendWidgetClickEvent({
                            widget_name: `home-page-level-2-grid-item-${slug}`,
                            widget_place: 'main',
                            trigger_type: `subcategory-level3-${slug}`,
                          })
                        }}
                      >
                        <p className="line-clamp-1">{name}</p>
                        <IconRightArrowLightWeight className="h-5 w-5" />
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </>
  )
}

export default Categories
